/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file BackgroundGalleryNoneOption
 * @author Liam Liden
 * @module Epic.VideoApp.Components.BackgroundSelector.BackgroundGalleryNoneOption
 */

import React, { FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useStrings } from "~/hooks";
import { useBackgroundToggle } from "~/hooks/backgroundEffects";
import error from "~/icons/error";
import { alertActions } from "~/state";
import { DefaultImageNames } from "~/types/backgrounds";
import { resolveClassName } from "~/utils/className";
import { ILocalStream } from "~/web-core/interfaces";
import BaseButton from "../Utilities/BaseButton";
import styles from "./BackgroundGalleryNoneOption.module.scss";

interface IProps {
	/** Preview track to remove background effects from */
	previewStream?: ILocalStream;

	ariaLabel?: string;
}

enum TokenNames {
	backgroundNoneOption = "BackgroundNoneOption",
}

export enum BackgroundGalleryNoneOptionTestIds {
	self = "BackgroundGalleryNOneOption",
}

/**
 * The BackgroundGalleryNoneOption component
 */
const BackgroundGalleryNoneOption: FC<IProps> = (props: IProps) => {
	const { previewStream, ariaLabel } = props;
	const { isEnabled, toggleProcessor } = useBackgroundToggle(DefaultImageNames.none, previewStream);

	const dispatch = useDispatch();

	const selectedClassName = resolveClassName(styles, {
		noneOption: true,
		selected: isEnabled,
	});

	const clearProcessor = useCallback(() => {
		toggleProcessor("on");
		dispatch(alertActions.setBackgroundName("cleared"));
	}, [dispatch, toggleProcessor]);

	const strings = useStrings("BackgroundGalleryNoneOption", Object.values(TokenNames));

	return (
		<div className={styles["noneOptionRow"]}>
			<BaseButton
				rawClass={selectedClassName}
				onClick={clearProcessor}
				text={strings[TokenNames.backgroundNoneOption]}
				icon={error}
				role="menuitemradio"
				aria-label={ariaLabel}
				aria-checked={isEnabled}
				data-testid={BackgroundGalleryNoneOptionTestIds.self}
			/>
		</div>
	);
};

BackgroundGalleryNoneOption.displayName = "BackgroundGalleryNoneOption";

export default BackgroundGalleryNoneOption;
